import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { PaymentPlanInitialState } from '../globalSlices/paymentPlanSlice';
import {
  getBuyerPaymentPlans,
  getSupplierPaymentPlans,
  createPaymentPlan,
  updatePaymentPlan,
  recordPaymentPlanPayment,
  cancelPaymentPlan,
  deletePaymentPlan,
  getMainBuyerPaymentPlans,
  cancelMainBuyerPaymentPlan,
  confirmMainBuyerPaymentPlan,
  updateMainBuyerPaymentPlanPaymentMethod,
} from '../services/paymentPlanService';
import { PaymentPlanWithUser, PaymentPlan } from '@dill/dill-shared';

export const getBuyerPaymentPlansExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'getBuyerPaymentPlans';
  builder.addCase(getBuyerPaymentPlans.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getBuyerPaymentPlans.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.buyerPaymentPlans = action.payload.data as PaymentPlanWithUser[];
      }
    }
  );
  builder.addCase(getBuyerPaymentPlans.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierPaymentPlansExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'getSupplierPaymentPlans';
  builder.addCase(getSupplierPaymentPlans.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getSupplierPaymentPlans.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.supplierPaymentPlans = action.payload.data as PaymentPlanWithUser[];
      }
    }
  );
  builder.addCase(getSupplierPaymentPlans.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createPaymentPlanExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'createPaymentPlan';
  builder.addCase(createPaymentPlan.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.error = null;
  });
  builder.addCase(
    createPaymentPlan.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        const newPlan = action.payload.data as PaymentPlanWithUser;
        state.buyerPaymentPlans = [newPlan, ...state.buyerPaymentPlans];
        state.supplierPaymentPlans = [newPlan, ...state.supplierPaymentPlans];
      }
    }
  );
  builder.addCase(createPaymentPlan.rejected, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    state.error = (action.payload as string) || 'Failed to create payment plan';
  });
};

export const updatePaymentPlanExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'updatePaymentPlan';
  builder.addCase(updatePaymentPlan.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(updatePaymentPlan.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(updatePaymentPlan.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

const replacePlanInLists = (state: PaymentPlanInitialState, updatedPlan: PaymentPlan) => {
  state.buyerPaymentPlans = state.buyerPaymentPlans.map((plan) =>
    plan.id === updatedPlan.id ? { ...plan, ...updatedPlan } : plan
  );
  state.supplierPaymentPlans = state.supplierPaymentPlans.map((plan) =>
    plan.id === updatedPlan.id ? { ...plan, ...updatedPlan } : plan
  );
};

export const recordPaymentPlanPaymentExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'recordPaymentPlanPayment';
  builder.addCase(recordPaymentPlanPayment.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.error = null;
  });
  builder.addCase(
    recordPaymentPlanPayment.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        replacePlanInLists(state, action.payload.data as PaymentPlan);
      }
    }
  );
  builder.addCase(recordPaymentPlanPayment.rejected, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    state.error = (action.payload as string) || 'Failed to record payment';
  });
};

export const cancelPaymentPlanExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'cancelPaymentPlan';
  builder.addCase(cancelPaymentPlan.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(cancelPaymentPlan.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(cancelPaymentPlan.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const deletePaymentPlanExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'deletePaymentPlan';
  builder.addCase(deletePaymentPlan.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(deletePaymentPlan.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(deletePaymentPlan.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getMainBuyerPaymentPlansExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'getMainBuyerPaymentPlans';
  builder.addCase(getMainBuyerPaymentPlans.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    getMainBuyerPaymentPlans.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        state.mainBuyerPaymentPlans = action.payload.data as PaymentPlan[];
      }
    }
  );
  builder.addCase(getMainBuyerPaymentPlans.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const cancelMainBuyerPaymentPlanExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'cancelMainBuyerPaymentPlan';
  builder.addCase(cancelMainBuyerPaymentPlan.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(cancelMainBuyerPaymentPlan.fulfilled, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(cancelMainBuyerPaymentPlan.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

const replacePlanInMainBuyerList = (state: PaymentPlanInitialState, updatedPlan: PaymentPlan) => {
  state.mainBuyerPaymentPlans = state.mainBuyerPaymentPlans.map((plan) =>
    plan.id === updatedPlan.id ? { ...plan, ...updatedPlan } : plan
  );
};

export const confirmMainBuyerPaymentPlanExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'confirmMainBuyerPaymentPlan';
  builder.addCase(confirmMainBuyerPaymentPlan.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.error = null;
  });
  builder.addCase(
    confirmMainBuyerPaymentPlan.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        replacePlanInMainBuyerList(state, action.payload.data as PaymentPlan);
      }
    }
  );
  builder.addCase(confirmMainBuyerPaymentPlan.rejected, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    state.error = (action.payload as string) || 'Failed to confirm payment plan';
  });
};

export const updateMainBuyerPaymentPlanPaymentMethodExtraReducer = (
  builder: ActionReducerMapBuilder<PaymentPlanInitialState>
) => {
  const reducerId = 'updateMainBuyerPaymentPlanPaymentMethod';
  builder.addCase(updateMainBuyerPaymentPlanPaymentMethod.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.error = null;
  });
  builder.addCase(
    updateMainBuyerPaymentPlanPaymentMethod.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload?.data) {
        replacePlanInMainBuyerList(state, action.payload.data as PaymentPlan);
      }
    }
  );
  builder.addCase(updateMainBuyerPaymentPlanPaymentMethod.rejected, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    state.error = (action.payload as string) || 'Failed to update payment method';
  });
};
